import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getAccountCapabilities } from '../../utils/mindmap/apiClient';
import MapsMenu from './MapsMenu';
import ThemeMenu from './ThemeMenu';
import ConnectionStyleMenu from './ConnectionStyleMenu';
import PlatformDashboard from './PlatformDashboard';
import UpgradeDialog from './UpgradeDialog';
import MapSearchPanel from '../mindmap/MapSearchPanel';
import NodeBreadcrumbs from './NodeBreadcrumbs';

const TopBar = ({
  onSignInClick,
  user,
  baseNodeTitle,
  onBaseNodeTitleClick,
  currentMap,
  mapFileActions,
  onSignOut,
  isVisible = true,
}) => {
  const accountMenuRef = useRef(null);
  const searchInputRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [dashboardOpen, setDashboardOpen] = useState(false);
  const [account, setAccount] = useState(null);

  useEffect(() => {
    if (!user) {
      setAccount(null);
      return undefined;
    }
    let cancelled = false;
    getAccountCapabilities()
      .then((result) => {
        if (!cancelled) setAccount(result);
      })
      .catch(() => {
        // Plan details remain available on demand through the dialog.
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    const handleCapabilitiesChanged = (event) => {
      if (event.detail) setAccount(event.detail);
    };
    window.addEventListener('mindmappi:accountCapabilitiesChanged', handleCapabilitiesChanged);
    return () => {
      window.removeEventListener('mindmappi:accountCapabilitiesChanged', handleCapabilitiesChanged);
    };
  }, []);

  useEffect(() => {
    if (!menuOpen || typeof document === 'undefined') return undefined;

    const handlePointerDown = (event) => {
      if (
        accountMenuRef.current
        && !accountMenuRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen]);

  const storedDarkMode = useMemo(() => {
    if (typeof window === 'undefined') return true;
    try {
      const stored = window.localStorage.getItem('mindMapTheme');
      if (stored === 'light') return false;
      if (stored === 'dark') return true;
    } catch {
      // ignore
    }
    return true;
  }, []);
  const isDarkMode = mapFileActions?.appearance?.isDarkMode ?? storedDarkMode;
  const searchActions = mapFileActions?.search || null;
  const canSearchMap = Boolean(searchActions);
  const canToggleDarkMode = typeof mapFileActions?.appearance?.toggleDarkMode === 'function';
  const ideaInboxActions = mapFileActions?.ideaInbox || null;

  const barStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: 48,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 16px',
    backgroundColor: isDarkMode
      ? 'rgba(45, 45, 45, 0.7)'
      : 'rgba(255, 255, 255, 0.7)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
    zIndex: 200,
    color: isDarkMode ? '#f9fafb' : '#111827',
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(-12px)',
    pointerEvents: isVisible ? 'auto' : 'none',
    visibility: isVisible ? 'visible' : 'hidden',
    transition: isVisible
      ? 'opacity 220ms ease-out, transform 220ms ease-out, visibility 0s linear'
      : 'opacity 180ms ease-in, transform 180ms ease-in, visibility 0s linear 180ms',
  };

  const logoStyle = {
    border: 'none',
    padding: 0,
    background: 'transparent',
    color: 'inherit',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    flexShrink: 0,
    fontWeight: 800,
    letterSpacing: '0.04em',
    fontSize: 20,
    fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    cursor: 'pointer',
  };
  const betaStyle = {
    marginLeft: 1,
    alignSelf: 'flex-start',
    fontSize: 10,
    lineHeight: 1,
    fontStyle: 'italic',
    fontWeight: 600,
    letterSpacing: '0.02em',
    opacity: 0.78,
  };
  const helpMapId = process.env.REACT_APP_HELP_MAP_ID || 'HELP';
  const helpEnabled = process.env.REACT_APP_HELP_ENABLED === 'true';
  const bugReportUrl = process.env.REACT_APP_BUG_REPORT_URL || '';
  const handleLogoClick = () => {
    if (typeof window === 'undefined') return;
    window.location.assign('/');
  };
  const handleHelpClick = () => {
    if (typeof window === 'undefined') return;
    const url = `/?map=${encodeURIComponent(helpMapId)}`;
    window.location.assign(url);
  };
  const handleBugReportClick = () => {
    if (typeof window === 'undefined') return;
    if (bugReportUrl) {
      window.open(bugReportUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    window.location.href = 'mailto:info@mindmappi.com?subject=Mindmappi%20beta%20bug%20report';
  };

  const contextTitleStyle = {
    marginLeft: 8,
    padding: '3px 5px',
    border: 0,
    borderRadius: 5,
    background: 'transparent',
    color: 'inherit',
    fontSize: 13,
    opacity: 0.9,
    fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    maxWidth: 260,
    cursor: 'pointer',
  };

  const signInButtonStyle = {
    border: '1px solid #e5e7eb',
    borderRadius: 999,
    padding: '6px 12px',
    fontSize: 13,
    fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    background: isDarkMode ? '#1f2933' : 'white',
    color: isDarkMode ? '#f9fafb' : '#111827',
    cursor: 'pointer',
  };

  const signedInLabelStyle = {
    border: 'none',
    borderRadius: 999,
    padding: '4px 10px',
    fontSize: 13,
    fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    background: isDarkMode ? 'rgba(17, 24, 39, 0.85)' : 'rgba(243, 244, 246, 0.9)',
    color: isDarkMode ? '#f9fafb' : '#111827',
    cursor: 'pointer',
  };

  const avatarStyle = {
    width: 28,
    height: 28,
    borderRadius: '50%',
    background: isDarkMode ? '#111827' : '#e5e7eb',
    color: isDarkMode ? '#f9fafb' : '#111827',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 600,
    fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  };

  const menuStyle = {
    position: 'absolute',
    top: 40,
    right: 0,
    minWidth: 140,
    padding: 8,
    borderRadius: 8,
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.3), 0 4px 6px -2px rgba(0,0,0,0.25)',
    border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
    zIndex: 210,
  };

  const menuItemStyle = {
    fontSize: 13,
    padding: '6px 10px',
    borderRadius: 4,
    cursor: 'pointer',
    fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    color: isDarkMode ? '#e5e7eb' : '#111827',
  };

  const initials = useMemo(() => {
    if (!user || !user.displayName) return '';
    const parts = user.displayName.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '';
    const first = parts[0][0] || '';
    const last = parts.length > 1 ? (parts[parts.length - 1][0] || '') : '';
    const letters = `${first}${last}`.toUpperCase();
    return letters || '';
  }, [user]);

  const dashboardButtonStyle = {
    width: 28,
    height: 28,
    display: 'grid',
    placeItems: 'center',
    padding: 0,
    border: 0,
    borderRadius: '50%',
    background: isDarkMode ? '#111827' : '#e5e7eb',
    color: isDarkMode ? '#f9fafb' : '#111827',
    cursor: 'pointer',
  };
  const handleSearchInputKeyDown = (event) => {
    event.stopPropagation();
    if ((event.ctrlKey || event.metaKey) && String(event.key).toLowerCase() === 'f') {
      event.preventDefault();
      searchInputRef.current?.select();
      searchActions?.open?.({ focus: false });
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      searchActions?.close?.({ blur: true });
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      searchActions?.moveActive?.(1);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      searchActions?.moveActive?.(-1);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      searchActions?.selectActive?.();
    }
  };
  const handleSearchFocus = (event) => {
    event.stopPropagation();
    searchActions?.open?.({ focus: false });
    setMenuOpen(false);
  };
  const handleSearchBlur = (event) => {
    const searchControl = event.currentTarget.closest('.top-bar__map-search');
    if (searchControl && event.relatedTarget && searchControl.contains(event.relatedTarget)) {
      return;
    }
    searchActions?.close?.();
  };
  const handleToggleDarkMode = (event) => {
    event.stopPropagation();
    mapFileActions?.appearance?.toggleDarkMode?.();
    setMenuOpen(false);
  };
  const mapControlButtons = (
    <>
      {canSearchMap && (
        <div
          className="top-bar__map-search"
          data-export-ignore="true"
          style={{ position: 'relative' }}
        >
          <label
            title="Search map (Ctrl/Cmd+F)"
            style={{
              height: 30,
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              padding: '0 9px',
              border: `1px solid ${searchActions.isOpen
                ? (isDarkMode ? '#7dd3fc' : '#2563eb')
                : (isDarkMode ? '#52525b' : '#d4d4d8')}`,
              borderRadius: 999,
              background: isDarkMode
                ? 'rgba(17, 24, 39, 0.82)'
                : 'rgba(255, 255, 255, 0.9)',
              color: isDarkMode ? '#f9fafb' : '#111827',
              boxShadow: searchActions.isOpen
                ? `0 0 0 2px ${isDarkMode ? 'rgba(125, 211, 252, 0.18)' : 'rgba(37, 99, 235, 0.12)'}`
                : 'none',
              cursor: 'text',
            }}
          >
            <svg
              aria-hidden="true"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              style={{ flex: '0 0 auto', opacity: 0.72 }}
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m16.5 16.5 4.5 4.5" />
            </svg>
            <input
              ref={searchInputRef}
              type="search"
              role="combobox"
              value={searchActions.query}
              data-map-search-input="true"
              aria-label="Search map"
              aria-autocomplete="list"
              aria-expanded={searchActions.isOpen}
              aria-controls="map-search-results"
              aria-activedescendant={
                searchActions.isOpen && searchActions.results[searchActions.activeIndex]
                  ? `map-search-result-${searchActions.results[searchActions.activeIndex].node.id}`
                  : undefined
              }
              autoComplete="off"
              spellCheck="false"
              placeholder="Search ideas…"
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
              onChange={(event) => searchActions.setQuery?.(event.target.value)}
              onKeyDown={handleSearchInputKeyDown}
              style={{
                width: '100%',
                minWidth: 0,
                padding: 0,
                border: 0,
                outline: 0,
                background: 'transparent',
                color: 'inherit',
                fontSize: 13,
                fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              }}
            />
          </label>
          {searchActions.isOpen && (
            <MapSearchPanel
              query={searchActions.query}
              results={searchActions.results}
              activeIndex={searchActions.activeIndex}
              isDarkMode={isDarkMode}
              onActiveIndexChange={searchActions.setActiveIndex}
              onSelectResult={searchActions.selectResult}
            />
          )}
        </div>
      )}
      {canToggleDarkMode && (
        <button
          type="button"
          aria-label={isDarkMode ? 'Use light mode' : 'Use dark mode'}
          title={isDarkMode ? 'Use light mode' : 'Use dark mode'}
          style={dashboardButtonStyle}
          onClick={handleToggleDarkMode}
        >
          {isDarkMode ? '☀️' : '🌙'}
        </button>
      )}
    </>
  );

  const bugReportButtonStyle = {
    ...dashboardButtonStyle,
    border: `1px solid ${isDarkMode ? '#92400e' : '#f59e0b'}`,
    background: isDarkMode ? 'rgba(146, 64, 14, 0.35)' : '#fef3c7',
    color: isDarkMode ? '#fbbf24' : '#92400e',
    fontSize: 18,
    fontWeight: 800,
    fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  };

  return (
    <div
      className="top-bar"
      aria-hidden={!isVisible}
      data-viewport-occlusion="true"
      style={barStyle}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: '1 1 0', marginRight: 12 }}>
        <button type="button" style={logoStyle} onClick={handleLogoClick} aria-label="Open my Mindmappi map">
          <img
            src="/mindmappi-mark-transparent.png"
            width="28"
            height="28"
            alt=""
            aria-hidden="true"
            style={{ display: 'block', objectFit: 'contain' }}
          />
          <span className="top-bar__wordmark">mindmappi</span>
          <span className="top-bar__beta-label" style={betaStyle}>beta</span>
        </button>
        {mapFileActions?.breadcrumbs?.path?.length ? (
          <NodeBreadcrumbs
            path={mapFileActions.breadcrumbs.path}
            onNavigate={mapFileActions.breadcrumbs.navigate}
            isDarkMode={isDarkMode}
          />
        ) : baseNodeTitle && (
          <button
            type="button"
            style={contextTitleStyle}
            onClick={onBaseNodeTitleClick}
            aria-label={`Return to full map from ${baseNodeTitle}`}
            title="Return to full map"
          >
            {'> '}{baseNodeTitle}
          </button>
        )}
      </div>
      {user ? (
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            className="top-bar__secondary-action"
            aria-label="Report a bug"
            title="Report a bug"
            style={bugReportButtonStyle}
            onClick={(event) => {
              event.stopPropagation();
              handleBugReportClick();
              setMenuOpen(false);
            }}
          >
            !
          </button>
          {helpEnabled && <div
            className="top-bar__secondary-action"
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: isDarkMode ? '#111827' : '#e5e7eb',
              color: isDarkMode ? '#f9fafb' : '#111827',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              fontWeight: 700,
              fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              cursor: 'pointer',
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleHelpClick();
            }}
            title="Help"
          >
            ?
          </div>}
          {account?.staff?.dashboardAccess && (
            <button
              type="button"
              aria-label="Open beta dashboard"
              title="Beta dashboard"
              style={dashboardButtonStyle}
              onClick={(event) => {
                event.stopPropagation();
                setDashboardOpen(true);
              }}
            >
              <svg
                aria-hidden="true"
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 19V9" />
                <path d="M10 19V5" />
                <path d="M16 19v-7" />
                <path d="M22 19H2" />
              </svg>
            </button>
          )}
          {mapControlButtons}
          {ideaInboxActions && (
            <button
              type="button"
              aria-label={`Open idea inbox with ${ideaInboxActions.count || 0} ${ideaInboxActions.count === 1 ? 'idea' : 'ideas'}`}
              title="Idea inbox"
              style={{ ...dashboardButtonStyle, position: 'relative' }}
              onClick={(event) => {
                event.stopPropagation();
                ideaInboxActions.open?.();
                setMenuOpen(false);
              }}
            >
              <svg
                aria-hidden="true"
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 5h16v14H4z" />
                <path d="M4 14h4l2 2h4l2-2h4" />
              </svg>
              {ideaInboxActions.count > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: -5,
                    right: -6,
                    display: 'grid',
                    minWidth: 16,
                    height: 16,
                    padding: '0 3px',
                    placeItems: 'center',
                    borderRadius: 999,
                    background: '#2563eb',
                    color: '#fff',
                    fontSize: 9,
                    fontWeight: 800,
                    boxSizing: 'border-box',
                  }}
                >
                  {ideaInboxActions.count > 99 ? '99+' : ideaInboxActions.count}
                </span>
              )}
            </button>
          )}
          <ThemeMenu
            mapTheme={mapFileActions?.mapTheme}
            isDarkMode={isDarkMode}
          />
          <ConnectionStyleMenu
            connectionStyle={mapFileActions?.connectionStyle}
            isDarkMode={isDarkMode}
          />
          <MapsMenu
            currentMap={currentMap}
            isDarkMode={isDarkMode}
            mapFileActions={mapFileActions}
            onOpen={() => setMenuOpen(false)}
          />
          <div ref={accountMenuRef} style={{ position: 'relative' }}>
            <button
              type="button"
              aria-label="User menu"
              aria-expanded={menuOpen}
              style={signedInLabelStyle}
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen((open) => !open);
              }}
            >
              <div style={avatarStyle}>
                {initials || '✓'}
              </div>
            </button>
            {menuOpen && (
              <div
                style={menuStyle}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <button
                  type="button"
                  style={{
                    ...menuItemStyle,
                    width: '100%',
                    border: 0,
                    background: 'transparent',
                    textAlign: 'left',
                    fontWeight: 700,
                  }}
                  onClick={() => {
                    setPlanDialogOpen(true);
                    setMenuOpen(false);
                  }}
                >
                  Plan: {account?.plan?.label || 'View plans'}
                </button>
                {helpEnabled && <button
                  type="button"
                  className="top-bar__mobile-menu-item"
                  style={{
                    ...menuItemStyle,
                    width: '100%',
                    border: 0,
                    background: 'transparent',
                    textAlign: 'left',
                  }}
                  onClick={() => {
                    handleHelpClick();
                    setMenuOpen(false);
                  }}
                >
                  Help
                </button>}
                <button
                  type="button"
                  className="top-bar__mobile-menu-item"
                  style={{
                    ...menuItemStyle,
                    width: '100%',
                    border: 0,
                    background: 'transparent',
                    textAlign: 'left',
                  }}
                  onClick={() => {
                    handleBugReportClick();
                    setMenuOpen(false);
                  }}
                >
                  Report a bug
                </button>
                <a
                  href="/terms"
                  style={{ ...menuItemStyle, display: 'block', color: 'inherit', textDecoration: 'none' }}
                  onClick={() => setMenuOpen(false)}
                >
                  Terms
                </a>
                <a
                  href="/privacy"
                  style={{ ...menuItemStyle, display: 'block', color: 'inherit', textDecoration: 'none' }}
                  onClick={() => setMenuOpen(false)}
                >
                  Privacy
                </a>
                <div
                  style={menuItemStyle}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = isDarkMode ? '#1f2937' : '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  onClick={() => {
                    if (onSignOut) onSignOut();
                    setMenuOpen(false);
                  }}
                >
                  Sign out
                </div>
              </div>
            )}
          </div>
          <UpgradeDialog
            isOpen={planDialogOpen}
            isDarkMode={isDarkMode}
            onClose={() => setPlanDialogOpen(false)}
            onActivated={async (updatedAccount) => setAccount(updatedAccount)}
          />
          <PlatformDashboard
            isOpen={dashboardOpen}
            isDarkMode={isDarkMode}
            onClose={() => setDashboardOpen(false)}
          />
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {mapControlButtons}
          <ThemeMenu
            mapTheme={mapFileActions?.mapTheme}
            isDarkMode={isDarkMode}
          />
          <ConnectionStyleMenu
            connectionStyle={mapFileActions?.connectionStyle}
            isDarkMode={isDarkMode}
          />
          <button
            type="button"
            aria-label="Sign in / Sign up"
            style={signInButtonStyle}
            onClick={(e) => {
              e.stopPropagation();
              if (onSignInClick) onSignInClick();
            }}
          >
            <span className="top-bar__sign-in-full">Sign in / Sign up</span>
            <span className="top-bar__sign-in-compact">Sign in</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default TopBar;
